import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { ExpFormService } from '../../services/expForm.service';
import { PERMISSIONS } from '../../constants/permissions';
import { ExpRealizationStatus, IExpForm } from '../../interfaces/commercial.interface';
import { formatDate } from '../../utils/dateUtils';
import { sortByDateDescFutureLast } from '../../utils/listDefaults';
import { enumLabel } from '../../utils/garmentsLabels';

const STATUS_COLOR: Record<ExpRealizationStatus, 'default' | 'info' | 'success' | 'warning'> = {
    NOT_REALIZED: 'default', PARTIALLY_REALIZED: 'info', REALIZED: 'success', WRITTEN_OFF: 'warning',
};

const money = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** EXP register with a server-side "Overdue only" filter (never a client-side re-filter of an
 * already-fetched page -- the request always carries `overdueOnly=true` when checked) and an
 * aging chip built from the API's own `daysOutstanding` (R-7: never computed in the browser). */
const ExpFormList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<IExpForm[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ExpRealizationStatus | ''>('');
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [bank, setBank] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const load = async () => {
        try {
            setLoading(true);
            const res = await ExpFormService.list(institutionId, { page, size, status, bank: bank || undefined, overdueOnly });
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load EXP forms');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, status, overdueOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.expList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">EXP Forms</Typography>
                {canManage && (
                    <Button data-help="commercial.expList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/exp-form/new')}>
                        New EXP
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.expList.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    data-help="commercial.expList.statusFilter" select label="Status" size="small" value={status}
                    onChange={(e) => { setStatus(e.target.value as ExpRealizationStatus | ''); setPage(0); }}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="NOT_REALIZED">Not realized</MenuItem>
                    <MenuItem value="PARTIALLY_REALIZED">Partially realized</MenuItem>
                    <MenuItem value="REALIZED">Realized</MenuItem>
                    <MenuItem value="WRITTEN_OFF">Written off</MenuItem>
                </TextField>
                <TextField data-help="commercial.expList.bank" label="Bank" size="small" value={bank}
                    onChange={(e) => setBank(e.target.value)}
                    onBlur={() => { setPage(0); load(); }} />
                <FormControlLabel
                    data-help="commercial.expList.overdueOnly"
                    control={<Checkbox checked={overdueOnly}
                        onChange={(e) => { setOverdueOnly(e.target.checked); setPage(0); }} />}
                    label="Overdue only"
                />
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.expList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.expList.col.number">EXP Number</TableCell>
                                <TableCell data-help="commercial.expList.col.date">EXP Date</TableCell>
                                <TableCell data-help="commercial.expList.col.buyer">Buyer</TableCell>
                                <TableCell data-help="commercial.expList.col.bank">Bank</TableCell>
                                <TableCell align="right" data-help="commercial.expList.col.invoiceValue">Invoice Value</TableCell>
                                <TableCell align="right" data-help="commercial.expList.col.realized">Realized</TableCell>
                                <TableCell data-help="commercial.expList.col.status">Status</TableCell>
                                <TableCell data-help="commercial.expList.col.aging">Aging</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortByDateDescFutureLast(rows, x => x.expDate).map(r => (
                                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/exp-form/${r.id}`)}>
                                    <TableCell>{r.expNumber}</TableCell>
                                    <TableCell>{formatDate(r.expDate)}</TableCell>
                                    <TableCell>{r.buyerName}</TableCell>
                                    <TableCell>{r.bankName}</TableCell>
                                    <TableCell align="right">{r.currencyCode} {money(r.invoiceValue)}</TableCell>
                                    <TableCell align="right">{r.currencyCode} {money(r.realizedAmount)}</TableCell>
                                    <TableCell><Chip data-help="commercial.expList.status" size="small" label={enumLabel(r.realizationStatus)} color={STATUS_COLOR[r.realizationStatus]} /></TableCell>
                                    <TableCell>
                                        {r.overdueRealization && (
                                            <Chip data-help="commercial.expList.overdue" size="small" color="error" label={`${r.daysOutstanding ?? 0}d overdue`} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center">No EXP forms found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default ExpFormList;
